"use client";

import Link from "next/link";
import {
  Lock,
  Server,
  Eye,
  CheckCircle,
  Shield,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface SecurityFeature {
  icon: React.ReactNode;
  titleKey: string;
  contentKey: string;
}

export default function SecurityPage() {
  const { t } = useI18n();

  const features: SecurityFeature[] = [
    {
      icon: <Lock size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.security.feature.encryption.title",
      contentKey: "pages.legal.security.feature.encryption.content",
    },
    {
      icon: <Server size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.security.feature.infrastructure.title",
      contentKey: "pages.legal.security.feature.infrastructure.content",
    },
    {
      icon: <Eye size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.security.feature.audit.title",
      contentKey: "pages.legal.security.feature.audit.content",
    },
    {
      icon: <CheckCircle size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.security.feature.compliance.title",
      contentKey: "pages.legal.security.feature.compliance.content",
    },
    {
      icon: <Shield size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.security.feature.access.title",
      contentKey: "pages.legal.security.feature.access.content",
    },
    {
      icon: <Zap size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.security.feature.monitoring.title",
      contentKey: "pages.legal.security.feature.monitoring.content",
    },
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {t("pages.legal.security.title") || "Безопасность данных"}
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            {t("pages.legal.security.subtitle") ||
              "Как мы защищаем ваши финансовые данные в соответствии с требованиями 152-ФЗ и лучшими практиками"}
          </p>
          <p className="text-sm text-slate-500 mt-4">
            {t("pages.legal.security.lastUpdated") ||
              "Последнее обновление: 12 апреля 2026"}
          </p>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-4xl font-bold text-keiro-green mb-2">
              256-bit
            </div>
            <p className="text-slate-600">
              {t("pages.legal.security.signal.encryption") ||
                "Шифрование данных в пути и покое"}
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-keiro-green mb-2">
              ISO 27001
            </div>
            <p className="text-slate-600">
              {t("pages.legal.security.signal.certified") ||
                "Сертифицировано (на пути к сертификации)"}
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-keiro-green mb-2">
              24/7
            </div>
            <p className="text-slate-600">
              {t("pages.legal.security.signal.monitoring") ||
                "Мониторинг и реагирование на инциденты"}
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-slate-200">
        <p className="text-lg text-slate-600 leading-relaxed mb-6">
          {t("pages.legal.security.intro") ||
            `Безопасность ваших данных — приоритет номер один для Keiront.
            Финансовые данные отелей чувствительны и требуют защиты на самом высоком уровне.
            Мы используем передовые технологии и лучшие практики индустрии.`}
        </p>
      </section>

      {/* Architecture Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-8">
          {t("pages.legal.security.architecture.title") ||
            "Архитектура безопасности"}
        </h2>

        <div className="space-y-8">
          {/* Multi-tenant isolation */}
          <div className="bg-slate-50 rounded-lg p-8 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-900 mb-4">
              {t("pages.legal.security.architecture.tenant.title") ||
                "Schema-per-tenant архитектура"}
            </h3>
            <p className="text-slate-600 leading-relaxed">
              {t("pages.legal.security.architecture.tenant.content") ||
                `Каждый клиент (тенант) имеет полностью изолированную базу данных схему.
                Данные разных отелей и юридических лиц физически разделены на уровне PostgreSQL.
                Это означает, что даже в случае критической ошибки в коде, невозможно случайно получить доступ к данным другого клиента.`}
            </p>
          </div>

          {/* Encryption */}
          <div className="bg-slate-50 rounded-lg p-8 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-900 mb-4">
              {t("pages.legal.security.architecture.encryption.title") ||
                "Шифрование"}
            </h3>
            <div className="space-y-4 text-slate-600">
              <div>
                <p className="font-semibold text-slate-900 mb-2">
                  {t(
                    "pages.legal.security.architecture.encryption.transport"
                  ) || "В пути (TLS 1.3)"}
                </p>
                <p className="leading-relaxed">
                  {t(
                    "pages.legal.security.architecture.encryption.transportContent"
                  ) ||
                    "Все данные передаются между клиентом и сервером через защищённые HTTPS соединения с TLS 1.3. Это предотвращает перехват данных третьими сторонами."}
                </p>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-2">
                  {t("pages.legal.security.architecture.encryption.rest") ||
                    "В покое (AES-256)"}
                </p>
                <p className="leading-relaxed">
                  {t(
                    "pages.legal.security.architecture.encryption.restContent"
                  ) ||
                    "Чувствительные данные (пароли, ключи интеграций, контактная информация) шифруются на диске с использованием AES-256. Даже если кто-то получит физический доступ к серверам, данные будут бесполезны без ключей."}
                </p>
              </div>
            </div>
          </div>

          {/* Database Security */}
          <div className="bg-slate-50 rounded-lg p-8 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-900 mb-4">
              {t("pages.legal.security.architecture.database.title") ||
                "Безопасность базы данных"}
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t("pages.legal.security.architecture.database.content") ||
                `PostgreSQL работает в приватной VPC сети без доступа в интернет.
                Доступ ограничен только серверам приложения через защищённые соединения.
                Все SQL-запросы используют параметризованные запросы для предотвращения SQL-injection.`}
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12">
          {t("pages.legal.security.features.title") ||
            "Функции безопасности"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-lg p-8 hover:border-keiro-green/50 transition"
            >
              <div className="flex gap-4 mb-4">
                <div className="flex-shrink-0">{feature.icon}</div>
                <h3 className="text-lg font-bold text-slate-900">
                  {t(feature.titleKey) || feature.titleKey}
                </h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                {t(feature.contentKey) || feature.contentKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Compliance Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-8">
          {t("pages.legal.security.compliance.title") ||
            "Соответствие требованиям"}
        </h2>

        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              {t("pages.legal.security.compliance.fz152.title") ||
                "Федеральный закон № 152-ФЗ"}
            </h3>
            <p className="text-slate-600 leading-relaxed">
              {t("pages.legal.security.compliance.fz152.content") ||
                `Наша платформа полностью соответствует требованиям 152-ФЗ "О защите персональных данных".
                Все данные хранятся на серверах в Российской Федерации.
                Мы проводим регулярные аудиты безопасности и обновляем политики в соответствии с изменениями законодательства.`}
            </p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              {t("pages.legal.security.compliance.gdpr.title") || "GDPR"}
            </h3>
            <p className="text-slate-600 leading-relaxed">
              {t("pages.legal.security.compliance.gdpr.content") ||
                `Для клиентов в ЕС: платформа соответствует требованиям Общего регламента по защите данных (GDPR).
                Мы заключили соглашения об обработке данных и обеспечиваем права субъектов данных.`}
            </p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              {t("pages.legal.security.compliance.pci.title") ||
                "PCI DSS (в пути)"}
            </h3>
            <p className="text-slate-600 leading-relaxed">
              {t("pages.legal.security.compliance.pci.content") ||
                `Хотя платформа не хранит данные платёжных карт (токены хранятся в защищённых системах платёжных шлюзов),
                мы следуем стандартам безопасности PCI DSS в обработке платёжной информации.`}
            </p>
          </div>
        </div>
      </section>

      {/* Incident Response */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-8">
          {t("pages.legal.security.incidentResponse.title") ||
            "План реагирования на инциденты"}
        </h2>

        <div className="space-y-4 text-slate-600">
          <p className="leading-relaxed">
            {t("pages.legal.security.incidentResponse.content1") ||
              "У нас есть документированный план реагирования на инциденты безопасности. В случае подозрения на нарушение безопасности:"}
          </p>

          <ol className="space-y-3 ml-4">
            <li className="flex gap-3">
              <span className="text-keiro-green font-bold flex-shrink-0">
                1.
              </span>
              <span>
                {t("pages.legal.security.incidentResponse.step1") ||
                  "Немедленное обнаружение и изоляция затронутых систем"}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-keiro-green font-bold flex-shrink-0">
                2.
              </span>
              <span>
                {t("pages.legal.security.incidentResponse.step2") ||
                  "Уведомление затронутых клиентов (в соответствии с законодательством)"}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-keiro-green font-bold flex-shrink-0">
                3.
              </span>
              <span>
                {t("pages.legal.security.incidentResponse.step3") ||
                  "Анализ корневой причины и восстановление"}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-keiro-green font-bold flex-shrink-0">
                4.
              </span>
              <span>
                {t("pages.legal.security.incidentResponse.step4") ||
                  "Имплементация мер для предотвращения повторения"}
              </span>
            </li>
          </ol>
        </div>
      </section>

      {/* Contact Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-slate-200">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">
          {t("pages.legal.security.contact.title") ||
            "Контакты по вопросам безопасности"}
        </h2>
        <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
          <p className="text-slate-600 mb-4">
            {t("pages.legal.security.contact.desc") ||
              "Если у вас есть опасения по поводу безопасности платформы или вы обнаружили потенциальную уязвимость:"}
          </p>
          <div className="space-y-2 font-mono text-sm text-slate-900">
            <p>Email: security@keiront.io</p>
            <p className="text-slate-600 text-sm">
              {t("pages.legal.security.contact.responsible") ||
                "Ответ гарантирован в течение 24 часов"}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.legal.security.cta.title") ||
              "Всё ещё есть вопросы по безопасности?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.legal.security.cta.desc") ||
              "Наша команда безопасности готова ответить на любые вопросы"}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button asChild variant="default" className="h-12 px-8">
              <Link href="/contact">
                {t("pages.legal.security.cta.contact") ||
                  "Свяжитесь с нами"}
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-8">
              <Link href="/legal/privacy">
                {t("pages.legal.security.cta.privacy") ||
                  "Политика конфиденциальности"}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
